/**
 * UI-State des Voll-Page-Browsers: aktueller Ordner, Multi-Select, Ansicht (Liste/Kacheln),
 * Suche im aktuellen Ordner. Rein lokaler View-State (kein Server-State) — Ansicht wird in
 * localStorage gespiegelt, damit der User seine Präferenz behält.
 */
import { useCallback, useState } from 'react';
import { useAkteSelection } from './useAkteSelection';

export type AkteViewMode = 'list' | 'tiles';

const VIEW_MODE_KEY = 'stayon.akte.viewMode';

function readViewMode(): AkteViewMode {
  if (typeof window === 'undefined') return 'list';
  return (localStorage.getItem(VIEW_MODE_KEY) as AkteViewMode | null) ?? 'list';
}

export function useAkteBrowser() {
  const [folderPath, setFolderPath] = useState('');
  const [viewMode, setViewModeState] = useState<AkteViewMode>(readViewMode);
  const [search, setSearchState] = useState('');
  const [fachansicht, setFachansicht] = useState(false);
  const selection = useAkteSelection();
  const { clear: clearSelection } = selection;

  /** Navigiert in einen Ordner und räumt Auswahl + Suche + Fachansicht auf. */
  const navigateTo = useCallback(
    (path: string) => {
      setFolderPath(path);
      clearSelection();
      setSearchState('');
      setFachansicht(false);
    },
    [clearSelection],
  );

  // Suche ist wie Navigation ein View-Wechsel (die sichtbare Datei-Menge ändert sich) — die
  // Auswahl mitzuziehen wäre inkonsistent (Toolbar-Anzahl/Größe/„Alle" bezögen sich auf eine
  // andere Menge als die sichtbare). Deshalb räumt jede Query-Änderung die Selektion auf.
  const setSearch = useCallback(
    (q: string) => {
      clearSelection();
      setSearchState(q);
    },
    [clearSelection],
  );

  const setViewMode = useCallback((mode: AkteViewMode) => {
    setViewModeState(mode);
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
      // localStorage deaktiviert (Privatmodus) — Ansicht bleibt in-memory.
    }
  }, []);

  return {
    folderPath,
    selection,
    viewMode,
    search,
    fachansicht,
    setFachansicht,
    navigateTo,
    setViewMode,
    setSearch,
  };
}
